import { DailySnapshot, HourlySnapshot } from '@/core/tokens';
import { isAddressWeth } from '@/domain/ticks/helpers';
import {
  NFTX_UNISWAP_SUBGRAPH,
  NftxV3Uniswap,
  createQuery,
  getChainConstant,
  querySubgraph,
} from 'nftx.js';
import { parseEther } from 'viem';

const fetchSnapshots = async ({
  poolIds,
  network,
}: {
  poolIds: string[];
  network: number;
}) => {
  const q = createQuery<NftxV3Uniswap.Query>();

  const dailyQuery = q.poolDayDatas
    .where((w) => [w.pool.in(poolIds)])
    .orderBy('date')
    .orderDirection('desc')
    .select((s) => [
      s.date,
      s.feesETH,
      s.volumeToken0,
      s.volumeToken1,
      s.tvlETH,
      s.tick,
      s.liquidity,
      s.pool((pool) => [pool.inputTokens((token) => [token.id]), pool.id]),
    ]);
  const hourlyQuery = q.poolHourDatas
    .where((w) => [w.pool.in(poolIds)])
    .orderBy('periodStartUnix')
    .orderDirection('desc')
    .select((s) => [
      s.periodStartUnix,
      s.feesETH,
      s.volumeToken0,
      s.volumeToken1,
      s.tvlETH,
      s.tick,
      s.liquidity,
      s.pool((pool) => [pool.inputTokens((token) => [token.id]), pool.id]),
    ]);

  const query = dailyQuery.combine(hourlyQuery);

  const data = await querySubgraph({
    url: getChainConstant(NFTX_UNISWAP_SUBGRAPH, network),
    query,
  });

  const dailySnapshots = data.poolDayDatas.map((x): DailySnapshot => {
    const isWeth0 = isAddressWeth(x.pool.inputTokens[0].id, network);
    const timestamp = Number(x.date);
    const day = Math.floor(timestamp / 60 / 60 / 24);

    return {
      type: 'day',
      timestamp,
      day,
      poolId: x.pool.id,
      token0: x.pool.inputTokens[0].id,
      token1: x.pool.inputTokens[1].id,
      activeLiquidity: BigInt(x.liquidity),
      tick: BigInt(x.tick ?? '0'),
      dailyTotalRevenue: parseEther(x.feesETH),
      dailyVolume: parseEther(isWeth0 ? x.volumeToken0 : x.volumeToken1),
      totalValueLocked: parseEther(x.tvlETH),
    };
  });

  const hourlySnapshots = data.poolHourDatas.map((x): HourlySnapshot => {
    const isWeth0 = isAddressWeth(x.pool.inputTokens[0].id, network);
    const timestamp = Number(x.periodStartUnix);
    const hour = Math.floor(timestamp / 60 / 60);

    return {
      type: 'hour',
      timestamp: timestamp,
      hour,
      poolId: x.pool.id,
      token0: x.pool.inputTokens[0].id,
      token1: x.pool.inputTokens[1].id,
      activeLiquidity: BigInt(x.liquidity),
      tick: BigInt(x.tick ?? '0'),
      hourlyTotalRevenue: parseEther(x.feesETH),
      hourlyVolume: parseEther(isWeth0 ? x.volumeToken0 : x.volumeToken1),
      totalValueLocked: parseEther(x.tvlETH),
    };
  });

  return { dailySnapshots, hourlySnapshots };
};

export default fetchSnapshots;
